import { ReactNode } from 'react';
import { FieldDef } from '../../../shared/CustomFormV3/types';
import {
  INJURY_BODY_ZONE,
  INJURY_BODY_ZONE_LABEL,
  INJURY_ONSET,
  INJURY_SEVERITY,
  INJURY_CONTEXT,
  INJURY_MECHANISM,
  INJURY_TYPE_EXTENDED,
} from '../../../enums/injuries';
import { LATERALITY, INJURY_TYPE } from '../../../enums/testing';
import styles from './InjuryRegistrationForm.module.scss';

// ─────────────────────────────────────────────────────────────────────────────
// Config statica del form registrazione/modifica infortunio: opzioni dei
// select e layout dei campi. Estratta dal componente perché non dipende da
// props/stato — vive qui per tenere InjuryRegistrationForm.tsx snello.
// ─────────────────────────────────────────────────────────────────────────────

export const BODY_ZONE_OPTIONS = Object.values(INJURY_BODY_ZONE).map((v) => ({
  value: v,
  label: INJURY_BODY_ZONE_LABEL[v],
}));

export const SEVERITY_OPTIONS = [
  { value: INJURY_SEVERITY.SLIGHT, label: 'Leggero (Slight)' },
  { value: INJURY_SEVERITY.MINIMAL, label: 'Minimo (Minimal)' },
  { value: INJURY_SEVERITY.MILD, label: 'Lieve (Mild)' },
  { value: INJURY_SEVERITY.MODERATE, label: 'Moderato (Moderate)' },
  { value: INJURY_SEVERITY.SEVERE, label: 'Grave (Severe)' },
  { value: INJURY_SEVERITY.CAREER_ENDING, label: 'Fine carriera' },
  { value: INJURY_SEVERITY.DA_VALUTARE, label: 'Da valutare' },
];

export const ONSET_OPTIONS = [
  { value: INJURY_ONSET.ACUTO, label: 'Acuto' },
  { value: INJURY_ONSET.GRADUALE, label: 'Graduale' },
  { value: INJURY_ONSET.RECIDIVA, label: 'Recidiva' },
];

export const INJURY_TYPE_OPTIONS = [
  { value: INJURY_TYPE_EXTENDED.MUSCOLARE, label: 'Muscolare' },
  { value: INJURY_TYPE_EXTENDED.TENDINEO, label: 'Tendineo' },
  { value: INJURY_TYPE_EXTENDED.LEGAMENTOSO, label: 'Legamentoso' },
  { value: INJURY_TYPE_EXTENDED.OSSEO_FRATTURA, label: 'Osseo / Frattura' },
  { value: INJURY_TYPE_EXTENDED.CONTUSIONE, label: 'Contusione' },
  { value: INJURY_TYPE_EXTENDED.DISTORSIONE, label: 'Distorsione' },
  { value: INJURY_TYPE_EXTENDED.LACERAZIONE, label: 'Lacerazione' },
  { value: INJURY_TYPE_EXTENDED.OVERUSE, label: 'Overuse' },
  { value: INJURY_TYPE_EXTENDED.LOMBALGIA, label: 'Lombalgia' },
  { value: INJURY_TYPE_EXTENDED.CONCUSSIONE, label: 'Concussione' },
  { value: INJURY_TYPE_EXTENDED.ALTRO, label: 'Altro' },
];

export const CONTEXT_OPTIONS = [
  { value: INJURY_CONTEXT.GARA, label: 'Partita' },
  { value: INJURY_CONTEXT.ALLENAMENTO, label: 'Allenamento' },
  { value: INJURY_CONTEXT.RISCALDAMENTO, label: 'Riscaldamento' },
  { value: INJURY_CONTEXT.FUORI_CAMPO, label: 'Extra-sportivo' },
];

export const MECHANISM_OPTIONS = [
  { value: INJURY_MECHANISM.NON_CONTATTO_SPRINT, label: 'Non contatto – sprint' },
  { value: INJURY_MECHANISM.CAMBIO_DIREZIONE, label: 'Cambio di direzione' },
  { value: INJURY_MECHANISM.CONTATTO, label: 'Contatto' },
  { value: INJURY_MECHANISM.OVERUSE, label: 'Overuse' },
  { value: INJURY_MECHANISM.ALTRO, label: 'Altro' },
];

export const SIDE_OPTIONS = [
  { value: LATERALITY.DX, label: 'Destra' },
  { value: LATERALITY.SX, label: 'Sinistra' },
  { value: LATERALITY.BILATERAL, label: 'Bilaterale' },
];

export const RTP_TYPE_OPTIONS = Object.values(INJURY_TYPE).map((v) => ({
  value: v,
  label: v.toUpperCase(),
}));

// ─── Intestazioni di sezione ─────────────────────────────────────────────────
// Prima erano 4 <p> con lo stesso style inline copiato, incluso il font-family
// 'Satoshi, sans-serif' — qui un'unica classe tokenizzata via CSS module.
const renderSectionHeader = (label: string, isFirst = false): ReactNode => (
  <p className={isFirst ? `${styles.sectionHeader} ${styles.sectionHeaderFirst}` : styles.sectionHeader}>
    {label}
  </p>
);

// ─── Layout completo del form ────────────────────────────────────────────────
export const INJURY_FORM_LAYOUT: FieldDef[] = [
  // ─── Sezione: Generali ───────────────────────────────────────────────
  {
    type: 'custom',
    key: '_sec1',
    size: 12,
    render: () => renderSectionHeader('Generali', true),
  },
  {
    type: 'single-select',
    key: 'bodyZone',
    label: 'Zona corporea',
    size: 6,
    options: BODY_ZONE_OPTIONS,
  },
  {
    type: 'single-select',
    key: 'laterality',
    label: 'Lato',
    size: 6,
    options: SIDE_OPTIONS,
  },
  {
    type: 'date',
    key: 'injuryDate',
    label: 'Data infortunio',
    size: 6,
    dateFormat: 'dd/MM/yyyy',
  },
  {
    type: 'date',
    key: 'expectedReturnDate',
    label: 'Ritorno previsto',
    size: 6,
    dateFormat: 'dd/MM/yyyy',
    isClearable: true,
  },
  // ─── Sezione: Clinico ────────────────────────────────────────────────
  {
    type: 'custom',
    key: '_sec2',
    size: 12,
    render: () => renderSectionHeader('Clinico'),
  },
  {
    type: 'single-select',
    key: 'severityEstimated',
    label: 'Gravità',
    size: 6,
    options: SEVERITY_OPTIONS,
  },
  {
    type: 'single-select',
    key: 'injuryType',
    label: 'Tipo lesione',
    size: 6,
    options: INJURY_TYPE_OPTIONS,
  },
  {
    type: 'single-select',
    key: 'onset',
    label: 'Insorgenza',
    size: 6,
    options: ONSET_OPTIONS,
  },
  {
    type: 'text-input',
    key: 'diagnosis',
    label: 'Diagnosi',
    size: 12,
    placeholder: 'es. Distorsione legamento crociato anteriore',
  },
  {
    type: 'text-input',
    key: 'treatingPhysio',
    label: 'Fisioterapista',
    size: 6,
    placeholder: 'Nome del professionista',
  },
  // ─── Sezione: Contesto ───────────────────────────────────────────────
  {
    type: 'custom',
    key: '_sec3',
    size: 12,
    render: () => renderSectionHeader('Contesto'),
  },
  {
    type: 'single-select',
    key: 'context',
    label: 'Contesto',
    size: 6,
    options: CONTEXT_OPTIONS,
  },
  {
    type: 'single-select',
    key: 'mechanism',
    label: 'Meccanismo',
    size: 6,
    options: MECHANISM_OPTIONS,
  },
  {
    type: 'text-area',
    key: 'description',
    label: 'Note libere',
    size: 12,
    placeholder: 'Descrizione dell\'evento, dinamica, osservazioni...',
  },
  // ─── Sezione: Opzioni avanzate ───────────────────────────────────────
  {
    type: 'custom',
    key: '_sec4',
    size: 12,
    render: () => renderSectionHeader('Opzioni avanzate'),
  },
  {
    type: 'boolean',
    key: 'createRtpProtocol',
    label: 'Crea protocollo RTP',
    size: 12,
  },
  {
    type: 'single-select',
    key: 'rtpInjuryType',
    label: 'Tipo lesione RTP',
    size: 6,
    options: RTP_TYPE_OPTIONS,
    visibleIf: (deps) => !!deps.createRtpProtocol,
    deps: ['createRtpProtocol'],
  },
];
